import './ChartComponent.sass';
import './datepicker.css';
import HighStock from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { usePrevious, useWindowSize } from 'react-use';
import DatePicker, { registerLocale } from 'react-datepicker';
import moment from 'moment';

import tw from 'date-fns/locale/zh-TW';
registerLocale('zh-TW', tw);

var digit = 3;

function getWeek(date) {
	var day = date.getDay();

	switch (day) {
		case 0:
			return '星期日';
		case 1:
			return '星期一';
		case 2:
			return '星期二';
		case 3:
			return '星期三';
		case 4:
			return '星期四';
		case 5:
			return '星期五';
		case 6:
			return '星期六';
	}
}

const ChartComponent = ({ graphData }) => {
	const [info, setInfo] = useState({
		dataMax: {
			data: '',
			date: '',
		},
		dataMin: {
			data: '',
			date: '',
		},
		max: {
			data: '',
			date: '',
		},
		min: {
			data: '',
			date: '',
		},
	});

	useEffect(() => {
		const dataMax = graphData.reduce((prev, current) => {
			if (prev === null) return current;
			if (current[1] > prev[1]) return current;
			return prev;
		}, null);

		const dataMin = graphData.reduce((prev, current) => {
			if (prev === null) return current;
			if (current[1] < prev[1]) return current;
			return prev;
		}, null);

		setInfo((prevValue) => {
			return {
				...prevValue,
				dataMax: {
					data: dataMax?.[1] || '0.00',
					date: dataMax?.[0] ? moment(dataMax[0]).format('(YYYY/MM/DD)') : '',
				},
				dataMin: {
					data: dataMin?.[1] || '0.00',
					date: dataMin?.[0] ? moment(dataMin[0]).format('(YYYY/MM/DD)') : '',
				},
			};
		});
	}, [graphData]);

	const updateInfo = useCallback((data) => {
		const oriD = data.target.series[0].data;

		const list = oriD.filter((item) => {
			return item.x >= data.min && item.x <= data.max;
		});

		const max = list.reduce((prev, current) => {
			if (prev === null) return current;
			if (current.y > prev.y) return current;
			return prev;
		}, null);

		const min = list.reduce((prev, current) => {
			if (prev === null) return current;
			if (current.y < prev.y) return current;
			return prev;
		}, null);

		setInfo((prevValue) => {
			return {
				...prevValue,
				max: {
					data: max?.y || 0,
					date: max?.x ? moment(max.x).format('(YYYY/MM/DD)') : '',
				},
				min: {
					data: min?.y || 0,
					date: min?.x ? moment(min.x).format('(YYYY/MM/DD)') : '',
				},
			};
		});
	}, []);

	const options = useMemo(() => {
		return {
			chart: {
				events: {
					redraw: function () {
						setStartEndDates();
					},
				},
				panning: true,
				pinchType: '',
				marginTop: -35,
				backgroundColor: null,
				resetZoomButton: {
					theme: {
						display: 'none',
					},
				},
				type: 'line',
			},
			tooltip: {
				useHTML: true,
				followTouchMove: true,
				backgroundColor: '#FFF',
				borderRadius: 5,
				crosshairs: {
					color: '#DFDFDF',
				},
				formatter: function () {
					var thisx;
					var thisy;
					if (this.point) {
						thisx = this.point.x;
						thisy = this.point.y;
					} else {
						thisx = this.x;
						thisy = this.y;
					}
					return "<div style='width: 140px; white-space:normal;'>" + getWeek(new Date(thisx)) + ', ' + moment(thisx).format('YYYY/MM/DD') + '<br><span>淨值：</span><strong>' + thisy.toFixed(digit) + '</strong></div>';
				},
			},
			rangeSelector: {
				buttons: [
					{
						type: 'month',
						count: 1,
						text: '1月',
					},
					{
						type: 'month',
						count: 3,
						text: '3月',
					},
					{
						type: 'month',
						count: 6,
						text: '6月',
					},
					{
						type: 'year',
						count: 1,
						text: '1年',
					},
					{
						type: 'ytd',
						text: '今年',
					},
					{
						type: 'all',
						text: '全部',
					},
				],
			},
			yAxis: {
				opposite: false,
				labels: {
					x: -5,
					y: -2,
					style: {
						color: '#666',
						fontSize: '12px',
					},
				},
			},
			scrollbar: {
				enabled: true,
				barBackgroundColor: '#F2F2F2',
				liveRedraw: false,
			},
			xAxis: {
				minRange: 1,
				minPadding: 0,
				maxPadding: 0,
				lineColor: '#E1E1E1',
				events: {
					afterSetExtremes: function (data) {
						updateInfo(data);
					},
				},
				dateTimeLabelFormats: {
					second: '%Y/%m/%d<br/>%H:%M:%S',
					minute: '%Y/%m/%d<br/>%H:%M',
					hour: '%Y/%m/%d<br/>%H:%M',
					day: '%Y/%m/%d',
					// week: '%Y/%m/%d',
					week: '%Y/%m',
					month: '%Y/%m',
					year: '%Y',
				},
				labels: {
					style: {
						fontSize: '12px',
					},
				},
				range: 12 * 30 * 24 * 3600 * 1000,
			},
			navigator: {
				enabled: true,
				margin: 10,
				series: {
					dataGrouping: {
						smoothed: false,
					},
					lineWidth: 1,
					lineColor: '#002469',
					type: 'line',
				},
				maskFill: 'rgba(255, 255, 255, 0.2)',
				xAxis: {
					labels: {
						style: {
							fontSize: '10px',
						},
					},
				},
			},
			plotOptions: {
				series: {
					states: {
						hover: {
							enabled: true,
							halo: {
								size: 0,
							},
						},
					},
					dataGrouping: {
						enabled: false,
						approximation: 'open',
					},
				},
			},
			series: [
				{
					name: '淨值',
					color: '#002469',
					lineWidth: 1,
					fillColor: null,
					symbol: 'circle',
					marker: {
						fillColor: '#002469',
						lineColor: '#FFF',
						lineWidth: 1,
						radius: 2,
					},
					shadow: false,
				},
			],
			credits: {
				enabled: false,
			},
		};
	}, [updateInfo]);

	const [chartOptions, setChartOptions] = useState(options);

	const prevGraphData = usePrevious(graphData);

	const chart = useRef(null);

	const [currentRange, setCurrentRange] = useState(3);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');

	const setStartEndDates = () => {
		setTimeout(() => {
			const start = chart.current.chart.rangeSelector.getInputValue('min');
			const end = chart.current.chart.rangeSelector.getInputValue('max');
			const day = 1000 * 60 * 60 * 24;
			if (start) setStartDate(moment(start + day)._d);
			if (end) setEndDate(moment(end + day)._d);
		}, 1000);
	};

	const resetRange = useCallback(() => {
		chart.current.chart.rangeSelector.clickButton(3);

		const xAxis = chart.current.chart.xAxis[0];
		const extremes = xAxis.getExtremes();
		xAxis.setExtremes(extremes.dataMax - 12 * 30 * 24 * 3600 * 1000, extremes.dataMax);
	}, []);

	useEffect(() => {
		if ((!prevGraphData || !prevGraphData.length) && graphData.length) {
			setChartOptions({
				...chartOptions,
				series: [{ ...options.series[0], data: graphData }],
			});

			setTimeout(() => {
				resetRange();
			}, 300);
		}
	}, [graphData, prevGraphData, chartOptions, options, resetRange]);

	useEffect(() => {
		chart.current.chart.rangeSelector.clickButton(currentRange);
	}, [currentRange]);

	const changeStartDate = (value) => {
		setStartDate(value);
		const val = new Date(value).getTime();
		const max = new Date(endDate).getTime();
		chart.current.chart.xAxis[0].setExtremes(val, max);
	};

	const changeEndDate = (value) => {
		setEndDate(value);
		const val = new Date(value).getTime();
		const min = new Date(startDate).getTime();
		chart.current.chart.xAxis[0].setExtremes(min, val);
	};

	const { width } = useWindowSize();

	useEffect(() => {
		if (width < 768 && chartOptions.navigator.enabled) {
			setChartOptions((prevValue) => {
				return {
					...prevValue,
					navigator: {
						...prevValue.navigator,
						enabled: false,
					},
					scrollbar: {
						...prevValue.scrollbar,
						enabled: false,
					},
					chart: {
						...prevValue.chart,
						height: 275,
					},
				};
			});
		}

		if (width >= 768 && !chartOptions.navigator.enabled) {
			setChartOptions((prevValue) => {
				return {
					...prevValue,
					navigator: {
						...prevValue.navigator,
						enabled: true,
					},
					scrollbar: {
						...prevValue.scrollbar,
						enabled: true,
					},
					chart: {
						...prevValue.chart,
						height: 400,
					},
				};
			});
		}
	}, [width, chartOptions]);

	return (
		<div className='ChartComponent'>
			<div className='controls'>
				<div className='flex-select'>
					<div className='label'>顯示區間</div>
					<select
						value={currentRange}
						onChange={(e) => {
							setCurrentRange(e.target.value);
						}}>
						<option value={0}>1月</option>
						<option value={1}>3月</option>
						<option value={2}>6月</option>
						<option value={3}>1年</option>
						<option value={4}>今年</option>
						<option value={5}>全部</option>
					</select>
				</div>

				<div className='flex-inputs'>
					<DatePicker locale='zh-TW' selected={startDate} onChange={changeStartDate} dateFormat='yyyy/MM/dd' allowSameDay={true} />
					<div className='mid'>~</div>
					<DatePicker locale='zh-TW' selected={endDate} onChange={changeEndDate} dateFormat='yyyy/MM/dd' allowSameDay={true} />
				</div>
			</div>

			<HighchartsReact ref={chart} highcharts={HighStock} options={chartOptions} constructorType={'stockChart'} />

			<div className='info-flex m'>
				<div className='item-title m'>查詢期間</div>
				<div className='item-title m'>成立至今</div>
			</div>

			<div className='info-flex l'>
				<div className='item-wrap'>
					<div className='item'>
						<div className='num green'>{info.min.data}</div>
						<div className='date'>{info.min.date}</div>
						<div className='text'>
							<span className='l'>期間</span>最低
						</div>
						<div className='line'></div>
					</div>

					<div className='item'>
						<div className='num red'>{info.max.data}</div>
						<div className='date'>{info.max.date}</div>
						<div className='text'>
							<span className='l'>期間</span>最高
						</div>
						<div className='line'></div>
					</div>
				</div>

				<div className='item-wrap'>
					<div className='item'>
						<div className='num green'>{info.dataMin.data}</div>
						<div className='date'>{info.dataMin.date}</div>
						<div className='text'>
							<span className='l'>至今</span>最低
						</div>
						<div className='line'></div>
					</div>

					<div className='item'>
						<div className='num red'>{info.dataMax.data}</div>
						<div className='date'>{info.dataMax.date}</div>
						<div className='text'>
							<span className='l'>至今</span>最高
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ChartComponent;
